/**
 * Users Hooks (Application)
 *
 * React Query hooks for user data fetching.
 * Uses the repository pattern from clean architecture.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import { usersRepository } from '../../infrastructure/repositories';

import type { UserSearchRequest } from '../../domain/types/requests';
import type { UpdateUserRequest } from '../../domain/repositories';

// ============================================================================
// Query Keys Factory Pattern
// ============================================================================

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  search: (request?: UserSearchRequest) => [...userKeys.all, 'search', request] as const,
  options: () => [...userKeys.all, 'options'] as const,
  detail: (id: number) => [...userKeys.all, 'detail', id] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to search users with filters, sorting, and pagination
 */
export function useUserSearch(request: UserSearchRequest) {
  return useQuery({
    queryKey: userKeys.search(request),
    queryFn: () => usersRepository.searchUsers(request),
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to fetch search options (columns and presets)
 */
export function useUserSearchOptions() {
  return useQuery({
    queryKey: userKeys.options(),
    queryFn: () => usersRepository.getSearchOptions(),
    staleTime: 10 * 60 * 1000, // 10 minutes - options rarely change
  });
}

/**
 * Hook to fetch a single user by ID using paginated search
 * This matches the Flutter behavior of fetching via search with id filter
 */
export function useUserById(userId: number | null) {
  return useQuery({
    queryKey: userKeys.detail(userId ?? 0),
    queryFn: async () => {
      if (!userId) return null;

      const request: UserSearchRequest = {
        search: { text: '' },
        filters: [{ name: 'id', values: [userId] }],
        sorts: [],
        page: 1,
        per_page: 1,
      };

      const response = await usersRepository.searchUsers(request);
      return response.users[0] ?? null;
    },
    enabled: !!userId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to update a user
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateUserRequest) => usersRepository.updateUser(request),
    onSuccess: () => {
      // Invalidate all user queries (list, search, and detail) so any
      // open UserDetailPage also reflects the updated data
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}
